/**
 * 円形 progress ring + 残り秒数 — TOTP 用 (Feature 5)。
 *
 * 旧 `.totp-progress` 横バーを置き換える視認性の高い UI。
 *
 * 設計:
 *   - SVG 円弧で経過比率を可視化、stroke-dasharray + stroke-dashoffset で
 *     周長の `progress` 比率分を空にする (残り部分が彩色される)
 *   - 中央に残り秒数を整数で表示 (Bitwarden / 1Password 既視感)
 *   - 残り秒数 5 以下で stroke を warning (赤) に切替、視認性 + アクセシビリティ
 *   - `prefers-reduced-motion` で transition off — モーション過敏 user 配慮
 *   - 親が `role="progressbar"` を保持する想定で本 component は presentational
 *     (aria-hidden 寄り)。残り秒数は子テキストとして読み上げられる
 *
 * `progress` は 0..1 の経過比率 (0 = 周期開始、1 = 満了直前)。
 */

export interface TotpRingProps {
  /** 0..1 の経過比率 (0 = 周期開始、1 = 満了直前) */
  progress: number

  /** 残り ms */
  remainingMs: number

  /** ring サイズ (px)。default 36 */
  size?: number
}

const RADIUS = 14
const CIRC = 2 * Math.PI * RADIUS

export const TotpRing = ({ progress, remainingMs, size = 36 }: TotpRingProps): JSX.Element => {
  const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000))
  // dashoffset は「描画する弧の終端」。progress 0 (周期開始) で全周描画、
  // progress 1 (満了直前) で何も描画しない → 残り時間が「減っていく」アニメーション
  const dashOffset = CIRC * progress
  const isWarning = remainingSec <= 5

  return (
    <span
      className={`totp-ring${isWarning ? ' totp-ring-warning' : ''}`}
      aria-hidden="true"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox="0 0 32 32">
        {/* 背景円 */}
        <circle
          cx="16"
          cy="16"
          r={RADIUS}
          fill="none"
          strokeWidth="2.5"
          className="totp-ring-track"
        />
        {/* 進行円 (12 時方向起点に時計回り) */}
        <circle
          cx="16"
          cy="16"
          r={RADIUS}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          className="totp-ring-progress"
          strokeDasharray={CIRC}
          strokeDashoffset={dashOffset}
          transform="rotate(-90 16 16)"
        />
      </svg>
      <span className="totp-ring-label">{remainingSec}</span>
    </span>
  )
}
